import axios from "axios";
import uuid from "react-uuid";
import Config from "../../../../Utils/Config";

// Low-level access to the auto-scaffolded data API (contract C5) for the EvalV2_* tables
// and views. Every other api file builds on this one; components never import it.
//
// The scaffold's rules, each of which has produced a silent wrong answer before
// (memory: reference_scaffold_apiid_contract):
//   * /{table}/{id} resolves on the `apiid` column, NOT the primary key. A PUT keyed on
//     the pk returns 200 and changes nothing. So every row is written with pk === apiid,
//     minted here with react-uuid, and every PUT addresses apiid.
//   * POST returns a message, never the row or its id. Never read an id back from it.
//   * `?Column=value` filters are equality-only and an unknown column is IGNORED (the whole
//     table comes back). `list()` therefore re-applies its own filters to the rows it
//     receives, so a typo can only ever return fewer rows, never someone else's.
//   * A table that has not been created/scaffolded yet answers 404 with an HTML page.
//
// Plain axios + Config.DATA_API only. Never src/Utils/AxiosConfig.jsx (it attaches the .NET
// bearer and logs the user out on a 401 from a host that has nothing to do with it).
//
// The network helpers resolve to { ok:true, data } | { ok:false, error, status?, notFound? }
// and never throw. `unwrap()` turns an envelope back into a value-or-throw for composing
// several calls inside `run()`, which converts any throw back into an envelope.

const base = () => Config.DATA_API;

/** Primary key column per table (the value written to both the pk and apiid). */
export const TABLE_PK = {
  EvalV2_Template: "TemplateId",
  EvalV2_Section: "SectionId",
  EvalV2_Question: "QuestionId",
  EvalV2_QuestionCourse: "QuestionCourseId",
  EvalV2_TemplateVersion: "TemplateVersionId",
  EvalV2_Evaluation: "EvaluationId",
  EvalV2_Answer: "AnswerId",
  EvalV2_Remediation: "RemediationId",
  EvalV2_EmailLog: "EmailLogId",
  EvalV2_Area: "AreaId",
  EvalV2_AreaSite: "AreaSiteId",
  EvalV2_EvaluatorGrant: "GrantId",
};

const AUDIT = ["IsDeleted", "CreatedBy", "CreatedDate", "ModifiedBy", "ModifiedDate"];

/**
 * Writable columns per table, exactly as db/migrations/2026-10-03-evalv2-schema.sql creates
 * them. Writes are projected onto this list so a row that came from a view (DistrictName,
 * RemediationCount, ...) or carries UI-only fields can be saved without the scaffold
 * rejecting an unknown column.
 */
export const TABLE_COLUMNS = {
  EvalV2_Template: ["TemplateId", "apiid", "SchoolDistrictId", "IsSystem", "Name", "Description", "TargetRoleId", "Status", "PassThresholdPct", "CurrentVersionNo", "ClonedFromTemplateId", ...AUDIT],
  EvalV2_Section: ["SectionId", "apiid", "TemplateId", "Name", "SortOrder", ...AUDIT],
  EvalV2_Question: ["QuestionId", "apiid", "TemplateId", "SectionId", "QuestionText", "HelpText", "QuestionType", "Weight", "ScaleMin", "ScaleMax", "PassAt", "YesIsPass", "AllowNA", "IsRequired", "SortOrder", ...AUDIT],
  EvalV2_QuestionCourse: ["QuestionCourseId", "apiid", "QuestionId", "CourseId", "Source", "SortOrder", ...AUDIT],
  EvalV2_TemplateVersion: ["TemplateVersionId", "apiid", "TemplateId", "VersionNo", "PassThresholdPct", "SnapshotJson", "QuestionCount", "PublishedBy", "PublishedOn", ...AUDIT],
  EvalV2_Evaluation: ["EvaluationId", "apiid", "SchoolDistrictId", "TemplateId", "TemplateVersionId", "TemplateNameSnapshot", "EvaluateeUserId", "EvaluateeNameSnapshot", "EvaluateeEmailSnapshot", "EvaluateeRoleId", "EvaluateeSchoolId", "EvaluateeSchoolNameSnapshot", "EvaluateeJobTitleSnapshot", "EvaluatorUserId", "EvaluatorNameSnapshot", "Status", "EvaluationDate", "StartedOn", "CompletedOn", "ScorePct", "ScoredWeight", "PossibleWeight", "PassThresholdPct", "Passed", "FailingCount", "OverallComment", "AcknowledgedOn", "AcknowledgedName", "AcknowledgedVia", "AcknowledgedDisagree", "AcknowledgedComment", "AcknowledgedIp", "AcknowledgedUserAgent", "LastEmailedOn", "LastEmailedTo", "LastEmailedBy", ...AUDIT],
  EvalV2_Answer: ["AnswerId", "apiid", "EvaluationId", "QuestionId", "SectionNameSnapshot", "QuestionTextSnapshot", "HelpTextSnapshot", "QuestionType", "Weight", "ScaleMin", "ScaleMax", "PassAt", "YesIsPass", "AllowNA", "IsRequired", "SectionSortOrder", "SortOrder", "AnswerBool", "AnswerNumber", "AnswerText", "IsNA", "NormalizedScore", "IsFailing", "ReviewerNote", ...AUDIT],
  EvalV2_Remediation: ["RemediationId", "apiid", "EvaluationId", "AnswerId", "QuestionId", "EvaluateeUserId", "CourseId", "CourseTitleSnapshot", "UserCourseAllocationId", "DueDate", "AssignStatus", "AssignError", "AssignedBy", "AssignedOn", ...AUDIT],
  EvalV2_EmailLog: ["EmailLogId", "apiid", "EvaluationId", "Kind", "SentTo", "SentBy", "SentOn", "Status", "Error", "IncludedSignoffLink", ...AUDIT],
  EvalV2_Area: ["AreaId", "apiid", "SchoolDistrictId", "Name", "ManagerUserId", ...AUDIT],
  EvalV2_AreaSite: ["AreaSiteId", "apiid", "AreaId", "SchoolId", ...AUDIT],
  EvalV2_EvaluatorGrant: ["GrantId", "apiid", "SchoolDistrictId", "EvaluatorUserId", "ScopeType", "SchoolId", "AreaId", "Note", ...AUDIT],
};

const isNil = (v) => v === null || v === undefined;

export const nowIso = () => new Date().toISOString();

/** GUID / string compare, trimmed and case-insensitive. Two nils are NOT the same. */
export const same = (a, b) => {
  if (isNil(a) || isNil(b)) return false;
  return String(a).trim().toUpperCase() === String(b).trim().toUpperCase();
};

/** The rows of a scaffold response (axios response, bare array, or {data:[...]}). */
export const asArray = (resp) => {
  if (Array.isArray(resp)) return resp;
  if (resp && Array.isArray(resp.data)) return resp.data;
  if (resp && resp.data && Array.isArray(resp.data.data)) return resp.data.data;
  return [];
};

export const isTrue = (v) => v === true || v === 1 || v === "1" || v === "true" || v === "True";
export const isDeleted = (row) => Boolean(row) && isTrue(row.IsDeleted);
export const isLive = (row) => Boolean(row) && !isDeleted(row);

/**
 * The apiid to address a row by. Accepts a row (its apiid, else its pk when `table` is
 * given — rows written by this module have pk === apiid) or a bare id string.
 */
export const rowKey = (rowOrId, table) => {
  if (isNil(rowOrId)) return null;
  if (typeof rowOrId !== "object") {
    const s = String(rowOrId).trim();
    return s || null;
  }
  const pk = table ? TABLE_PK[table] : null;
  const v = rowOrId.apiid ?? (pk ? rowOrId[pk] : undefined);
  return isNil(v) || String(v).trim() === "" ? null : String(v).trim();
};

/** Project a row onto the table's real columns (unknown tables pass through untouched). */
export const pickColumns = (table, row) => {
  const cols = TABLE_COLUMNS[table];
  if (!cols || !row) return { ...(row || {}) };
  const out = {};
  for (const c of cols) if (Object.prototype.hasOwnProperty.call(row, c) && row[c] !== undefined) out[c] = row[c];
  return out;
};

// ── envelopes ──────────────────────────────────────────────────────────────

export const ok = (data, extra) => ({ ok: true, data, ...(extra || {}) });
export const fail = (error, extra) => ({ ok: false, error: error || "Something went wrong.", ...(extra || {}) });

/** A readable message from an axios error (server JSON error text wins). */
export const describeError = (err, what) => {
  const status = err?.response?.status;
  const body = err?.response?.data;
  if (status === 404) {
    return `${what || "This data"} is not available yet (404). The evaluation tables may not be set up on this database.`;
  }
  const server = body && typeof body === "object" ? body.error || body.message || body.title : null;
  if (typeof server === "string" && server.trim()) return server.trim();
  if (status) return `${what || "Request"} failed (HTTP ${status}).`;
  return err?.message || "Network error.";
};

const errorEnvelope = (err, what) =>
  fail(describeError(err, what), {
    status: err?.response?.status ?? null,
    ...(err?.response?.status === 404 ? { notFound: true } : {}),
  });

export class EnvelopeError extends Error {
  constructor(envelope) {
    super(envelope?.error || "Something went wrong.");
    this.name = "EnvelopeError";
    this.envelope = envelope;
  }
}

/** value of an ok envelope, else throws an EnvelopeError carrying it. */
export const unwrap = (env) => {
  if (env && env.ok) return env.data;
  throw new EnvelopeError(env);
};

/** Run fn; a returned value becomes ok(value), any throw becomes a failure envelope. */
export const run = async (fn) => {
  try {
    return ok(await fn());
  } catch (err) {
    if (err instanceof EnvelopeError) {
      const extra = {};
      for (const [k, v] of Object.entries(err.envelope || {})) {
        if (k !== "ok" && k !== "data" && k !== "error") extra[k] = v;
      }
      return fail(err.message, extra);
    }
    if (err?.isAxiosError || err?.response) return errorEnvelope(err);
    return fail(err?.message || String(err));
  }
};

/** Map with a concurrency cap; preserves order. fn may throw (the first throw rejects). */
export const mapLimit = async (items, limit, fn) => {
  const list = Array.from(items || []);
  const out = new Array(list.length);
  let next = 0;
  const workers = Array.from({ length: Math.max(1, Math.min(limit || 1, list.length)) }, async () => {
    while (next < list.length) {
      const i = next++;
      out[i] = await fn(list[i], i);
    }
  });
  await Promise.all(workers);
  return out;
};

// ── filtering ──────────────────────────────────────────────────────────────

const RESERVED = new Set(["limit", "top", "$top"]);

const looseEqual = (rowValue, wanted) => {
  if (isNil(wanted)) return isNil(rowValue);
  if (typeof wanted === "boolean" || wanted === "true" || wanted === "false") {
    const want = wanted === true || wanted === "true";
    return isTrue(rowValue) === want && !isNil(rowValue);
  }
  if (isNil(rowValue)) return false;
  if (typeof rowValue === "boolean") return isTrue(wanted) === rowValue;
  return same(rowValue, wanted);
};

/** Drop undefined/null/"" params so "undefined" never reaches the query string. */
const cleanParams = (params) =>
  Object.fromEntries(Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== ""));

/** Re-apply equality filters client-side (the scaffold silently ignores unknown columns). */
export const applyFilters = (rows, params) => {
  const filters = Object.entries(cleanParams(params)).filter(([k]) => !RESERVED.has(k));
  if (!filters.length) return rows;
  return rows.filter((r) => filters.every(([k, v]) => looseEqual(r?.[k], v)));
};

// ── network helpers ────────────────────────────────────────────────────────

/**
 * GET /{table}?params. Soft-deleted rows are dropped unless opts.includeDeleted.
 * -> {ok, data: rows[]}
 */
export async function list(table, params = {}, opts = {}) {
  const query = cleanParams(params);
  try {
    const resp = await axios.get(`${base()}/${table}`, { params: query });
    let rows = applyFilters(asArray(resp), query);
    if (!opts.includeDeleted) rows = rows.filter(isLive);
    return ok(rows);
  } catch (err) {
    return errorEnvelope(err, table);
  }
}

/**
 * One row by apiid (via the `apiid` column filter, so a missing row and a missing table can
 * be told apart). -> {ok, data: row|null}. Soft-deleted rows ARE returned; callers decide.
 */
export async function get(table, apiid) {
  const key = rowKey(apiid);
  if (!key) return fail(`${table}: an id is required.`);
  const res = await list(table, { apiid: key, limit: 1 }, { includeDeleted: true });
  if (!res.ok) return res;
  return ok(res.data[0] || null);
}

/**
 * POST a row. The pk and apiid are set to the same value (the row's own id when it has one,
 * else a freshly minted GUID). -> {ok, data: row as written}
 */
export async function insert(table, row) {
  const pk = TABLE_PK[table];
  const id = rowKey(row, table) || uuid();
  const body = pickColumns(table, {
    IsDeleted: false,
    CreatedDate: nowIso(),
    ...row,
    ...(pk ? { [pk]: id } : {}),
    apiid: id,
  });
  try {
    await axios.post(`${base()}/${table}`, body);
    return ok(body);
  } catch (err) {
    return errorEnvelope(err, table);
  }
}

/**
 * PUT /{table}/{apiid} with a partial patch. The pk and apiid are never sent in the body.
 * ModifiedDate is stamped unless the patch sets it. -> {ok, data: patch as sent (+apiid)}
 */
export async function update(table, apiid, patch) {
  const key = rowKey(apiid);
  if (!key) return fail(`${table}: an id is required to update.`);
  const pk = TABLE_PK[table];
  const body = pickColumns(table, { ModifiedDate: nowIso(), ...(patch || {}) });
  delete body.apiid;
  if (pk) delete body[pk];
  try {
    await axios.put(`${base()}/${table}/${key}`, body);
    return ok({ ...body, apiid: key, ...(pk ? { [pk]: key } : {}) });
  } catch (err) {
    return errorEnvelope(err, table);
  }
}

/** Soft delete: IsDeleted = 1 via PUT. Never a DELETE. */
export async function softDelete(table, apiid, by) {
  return update(table, apiid, { IsDeleted: true, ModifiedBy: isNil(by) ? null : by });
}

/**
 * Revive-don't-insert. Lists `table` by `params` (deleted rows included), finds the first
 * row `matchFn` accepts — preferring a live one — and:
 *   live match   -> PUTs the row's non-key fields when any differ        action 'existing'
 *   dead match   -> PUTs IsDeleted=0 plus the row's fields                action 'revived'
 *   no match     -> POSTs the row                                         action 'inserted'
 * -> {ok, data: {row, action}}
 */
export async function reviveOrInsert(table, matchFn, row, params = {}) {
  const listed = await list(table, params, { includeDeleted: true });
  if (!listed.ok) return listed;
  const matches = listed.data.filter((r) => matchFn(r));
  const liveMatch = matches.find(isLive);
  const target = liveMatch || matches[0];
  const pk = TABLE_PK[table];

  if (!target) {
    const ins = await insert(table, row);
    return ins.ok ? ok({ row: ins.data, action: "inserted" }) : ins;
  }

  const key = rowKey(target, table);
  const fields = pickColumns(table, row);
  delete fields.apiid;
  if (pk) delete fields[pk];
  delete fields.CreatedDate;
  delete fields.CreatedBy;

  if (liveMatch) {
    const IGNORE = new Set(["IsDeleted", "ModifiedBy", "ModifiedDate"]);
    const changed = Object.entries(fields).filter(
      ([k, v]) => !IGNORE.has(k) && !(String(target[k] ?? "") === String(v ?? "") || same(target[k], v))
    );
    if (!changed.length) return ok({ row: target, action: "existing" });
    const patch = Object.fromEntries(changed);
    if ("ModifiedBy" in fields) patch.ModifiedBy = fields.ModifiedBy;
    const up = await update(table, key, patch);
    return up.ok ? ok({ row: { ...target, ...patch }, action: "existing" }) : up;
  }

  const patch = { ...fields, IsDeleted: false };
  const up = await update(table, key, patch);
  return up.ok ? ok({ row: { ...target, ...patch }, action: "revived" }) : up;
}

export const DATA_API_BASE = () => base();
